import { useCallback, useEffect, useRef, useState } from "react";
import { askIncomeRun } from "./api";

const SUGGESTIONS = [
  "How was the total qualifying income calculated?",
  "Why is bonus income conditional or excluded?",
  "Walk me through the base income formula.",
  "What did the judge flag and why?",
];

/**
 * Per-run chatbot. Answers questions grounded in the run's artifacts
 * (result / derived income / field decisions) via the admin chat endpoint.
 * The backend builds context from the compact artifacts, so the huge raw
 * pipeline state never has to be sent.
 */
export default function ChatPanel({ jobId, env, loanId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = useCallback(
    async (text) => {
      const question = (text ?? input).trim();
      if (!question || sending) {
        return;
      }
      const nextMessages = [...messages, { role: "user", content: question }];
      setMessages(nextMessages);
      setInput("");
      setSending(true);
      try {
        const res = await askIncomeRun(jobId, nextMessages, env);
        const answer = res.success
          ? res.answer
          : res.reason || "Couldn't answer that.";
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: answer },
        ]);
      } catch (err) {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: `Error: ${err.message}` },
        ]);
      } finally {
        setSending(false);
      }
    },
    [input, sending, messages, jobId, env],
  );

  return (
    <div className="icr-chat">
      <div className="icr-chat__head">
        Ask about this run
        <small>Loan {loanId} · grounded in this run&apos;s data</small>
      </div>
      <div className="icr-chat__body" ref={bodyRef}>
        {messages.length === 0 && (
          <>
            <div className="icr-muted">
              Ask how any number was reached, why something was excluded, or
              what the judge did.
            </div>
            <div className="icr-chat__suggest">
              {SUGGESTIONS.map((q) => (
                <button
                  key={q}
                  className="icr-chip"
                  onClick={() => send(q)}
                  disabled={sending}
                >
                  {q}
                </button>
              ))}
            </div>
          </>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`icr-msg icr-msg--${m.role}`}>
            {m.content}
          </div>
        ))}
        {sending && (
          <div className="icr-msg icr-msg--assistant">Thinking…</div>
        )}
      </div>
      <div className="icr-chat__foot">
        <textarea
          className="icr-chat__input"
          rows={2}
          placeholder="Ask a question…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
        />
        <button
          className="icr-btn icr-btn--primary"
          onClick={() => send()}
          disabled={sending || !input.trim()}
        >
          Send
        </button>
      </div>
    </div>
  );
}
